import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  type AuthenticationResultType,
} from "@aws-sdk/client-cognito-identity-provider";
import type { AuthSession } from "./types";

const REGION = import.meta.env.VITE_AWS_REGION as string | undefined;
const CLIENT_ID = import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID as string | undefined;
const STORAGE_KEY = "conversa-smart:auth-session:v1";
let refreshInFlight: Promise<AuthSession> | null = null;

export type NewPasswordChallenge = {
  kind: "NEW_PASSWORD_REQUIRED";
  email: string;
  session: string;
};

export type SignInResult =
  | { kind: "AUTHENTICATED"; session: AuthSession }
  | NewPasswordChallenge;

function client() {
  if (!REGION || !CLIENT_ID) throw new Error("AUTH_NOT_CONFIGURED");
  return new CognitoIdentityProviderClient({ region: REGION });
}

export function authConfigured() {
  return Boolean(REGION && CLIENT_ID);
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  if (!CLIENT_ID) throw new Error("AUTH_NOT_CONFIGURED");
  const response = await client().send(
    new InitiateAuthCommand({
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: CLIENT_ID,
      AuthParameters: {
        USERNAME: email.trim().toLowerCase(),
        PASSWORD: password,
      },
    }),
  );

  if (response.ChallengeName === "NEW_PASSWORD_REQUIRED" && response.Session) {
    return {
      kind: "NEW_PASSWORD_REQUIRED",
      email: email.trim().toLowerCase(),
      session: response.Session,
    };
  }
  if (!response.AuthenticationResult) throw new Error(response.ChallengeName ?? "AUTHENTICATION_FAILED");
  const session = sessionFromResult(response.AuthenticationResult, email);
  persistSession(session);
  return { kind: "AUTHENTICATED", session };
}

export async function completeNewPassword(
  challenge: NewPasswordChallenge,
  newPassword: string,
): Promise<AuthSession> {
  if (!CLIENT_ID) throw new Error("AUTH_NOT_CONFIGURED");
  const response = await client().send(
    new RespondToAuthChallengeCommand({
      ChallengeName: "NEW_PASSWORD_REQUIRED",
      ClientId: CLIENT_ID,
      Session: challenge.session,
      ChallengeResponses: {
        USERNAME: challenge.email,
        NEW_PASSWORD: newPassword,
      },
    }),
  );
  if (!response.AuthenticationResult) throw new Error(response.ChallengeName ?? "PASSWORD_CHANGE_FAILED");
  const session = sessionFromResult(response.AuthenticationResult, challenge.email);
  persistSession(session);
  return session;
}

export async function restoreSession(): Promise<AuthSession | null> {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as AuthSession & { tenantId?: string };
    stored.legacyTenantId = stored.legacyTenantId ?? stored.tenantId;
    if (!sessionNeedsRefresh(stored)) return stored;
    return await refreshSession(stored, true);
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function signOut() {
  if (typeof window !== "undefined") window.localStorage.removeItem(STORAGE_KEY);
}

export function withActiveOrganization(session: AuthSession, organizationId: string): AuthSession {
  const persisted = readStoredSession();
  const base = persisted && persisted.email === session.email && persisted.expiresAt > session.expiresAt
    ? persisted
    : session;
  const next = { ...base, activeOrganizationId: organizationId };
  persistSession(next);
  return next;
}

export function sessionNeedsRefresh(session: AuthSession, now = Date.now()) {
  const tokenExpiry = tokenExpiresAt(session.idToken);
  const effectiveExpiry = tokenExpiry ? Math.min(session.expiresAt, tokenExpiry) : session.expiresAt;
  return effectiveExpiry <= now + 60_000;
}

export async function ensureFreshSession(session: AuthSession, force = false): Promise<AuthSession> {
  const persisted = readStoredSession();
  const candidate = persisted && persisted.email === session.email && persisted.expiresAt > session.expiresAt
    ? { ...persisted, activeOrganizationId: session.activeOrganizationId ?? persisted.activeOrganizationId }
    : session;
  if (!force && !sessionNeedsRefresh(candidate)) return candidate;
  return refreshSession(candidate, force);
}

export async function authenticatedFetch(
  url: string,
  session: AuthSession,
  init: RequestInit = {},
) {
  let activeSession = await ensureFreshSession(session);
  let response = await fetch(url, withAuthHeaders(init, activeSession));
  if (response.status !== 401 || !activeSession.refreshToken) return response;
  activeSession = await ensureFreshSession(activeSession, true);
  return fetch(url, withAuthHeaders(init, activeSession));
}

async function refreshSession(session: AuthSession, force: boolean) {
  if (!force && !sessionNeedsRefresh(session)) return session;
  if (!session.refreshToken || !CLIENT_ID) throw new Error("SESSION_EXPIRED");
  if (!refreshInFlight) {
    refreshInFlight = client().send(
      new InitiateAuthCommand({
        AuthFlow: "REFRESH_TOKEN_AUTH",
        ClientId: CLIENT_ID,
        AuthParameters: { REFRESH_TOKEN: session.refreshToken },
      }),
    ).then((response) => {
      if (!response.AuthenticationResult) throw new Error("SESSION_REFRESH_FAILED");
      return sessionFromResult(response.AuthenticationResult, session.email, session.refreshToken);
    }).finally(() => {
      refreshInFlight = null;
    });
  }
  const refreshed = await refreshInFlight;
  const next = {
    ...refreshed,
    activeOrganizationId: session.activeOrganizationId,
    legacyTenantId: session.legacyTenantId ?? refreshed.legacyTenantId,
  };
  persistSession(next);
  return next;
}

function withAuthHeaders(init: RequestInit, session: AuthSession): RequestInit {
  return {
    ...init,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${session.idToken}`,
      ...(session.activeOrganizationId ? { "X-Organization-Id": session.activeOrganizationId } : {}),
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  };
}

function tokenExpiresAt(token: string) {
  const claims = decodeJwt(token);
  const expiresAt = Number(claims.exp);
  return Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt * 1_000 : 0;
}

function readStoredSession() {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

function sessionFromResult(
  result: AuthenticationResultType,
  fallbackEmail: string,
  fallbackRefreshToken?: string,
): AuthSession {
  if (!result.IdToken || !result.AccessToken) throw new Error("TOKENS_MISSING");
  const claims = decodeJwt(result.IdToken);
  const groups = Array.isArray(claims["cognito:groups"])
    ? (claims["cognito:groups"] as unknown[]).map(String)
    : [];
  const expiresIn = Number(result.ExpiresIn ?? 3_600);
  const tokenExpiry = Number(claims.exp);
  return {
    email: String(claims.email ?? fallbackEmail),
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    refreshToken: result.RefreshToken ?? fallbackRefreshToken,
    expiresAt: Number.isFinite(tokenExpiry) && tokenExpiry > 0
      ? tokenExpiry * 1_000
      : Date.now() + expiresIn * 1_000,
    legacyTenantId: String(claims["custom:tenant_id"] ?? "") || undefined,
    groups,
  };
}

function decodeJwt(token: string): Record<string, unknown> {
  const payload = token.split(".")[1];
  if (!payload) return {};
  const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  try {
    return JSON.parse(decodeURIComponent(escape(atob(padded)))) as Record<string, unknown>;
  } catch {
    try {
      return JSON.parse(atob(padded)) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
}

function persistSession(session: AuthSession) {
  if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}
